import { Button, Icon, Modal, Text, Title } from '@wyz/ui';
import { sortBy } from 'lodash-es';
import { Link } from 'react-router';
import { Trans } from '~/common/context';
import { WcwCartHelpers } from '~/purchase/helpers';
import {
  IWcwCartItem,
  IWcwCartValidationErrorResponse,
} from '~/purchase/types';

type ItemRowProps = {
  item: IWcwCartItem;
  reason: 'UNAVAILABLE' | 'ERROR';
};

function ItemRow({ item, reason }: ItemRowProps) {
  return (
    <tr>
      <td className='p-3'>
        <Text
          heading='normal'
          weight='bold'
          color='tertiary'
          className='text-uppercase'
        >
          {item.wheel.name}
        </Text>
        <Text heading='xs' color='secondary-gray'>
          <Trans
            i18nKey='common.tyre.ref_value'
            values={{ value: item.wheel.reference }}
          />
        </Text>
        <Text heading='xs' color='secondary-gray'>
          {`${item.tyre.brand} · ${item.tyre.dimension}`}
        </Text>
      </td>
      <td className='p-3'>
        <Text heading='normal' weight='semi-bold' color='secondary'>
          {item.supplier.name}
        </Text>
      </td>
      <td className='p-3' align='right'>
        <Text heading='normal' weight='semi-bold' color='danger'>
          <Trans
            i18nKey={
              reason === 'UNAVAILABLE'
                ? 'app.purchase.cart_page.unavailable_modal.unavailable_product'
                : 'app.purchase.cart_page.unavailable_modal.technical'
            }
          />
        </Text>
        {reason === 'UNAVAILABLE' && (
          <Text
            heading='xs'
            color='secondary-gray'
            weight='semi-bold'
            strikethrough
          >
            {item.quantity}
          </Text>
        )}
      </td>
    </tr>
  );
}

type Props = {
  items: IWcwCartValidationErrorResponse['errors'];
  onClose: () => void;
};

function WcwUnavailableItemsModal({ items, onClose }: Props) {
  return (
    <Modal.Container
      onClose={onClose}
      open={items.length !== 0}
      centered
      size='lg'
    >
      <Modal.Header closeButton variant='white'>
        <Title
          heading='xl'
          weight='bold'
          color='secondary'
          className='text-center'
          wrap
        >
          <Trans i18nKey='app.purchase.cart_page.unavailable_modal.title' />
        </Title>
      </Modal.Header>
      <Modal.Body>
        <div
          className='bg-neutral-grey-1 border rounded border-neutral-grey-4'
          style={{ maxHeight: '50vh', overflowY: 'auto' }}
        >
          <table className='w-100'>
            <thead>
              <tr>
                <td className='p-3' style={{ width: '55%' }}>
                  <Text heading='xs' color='secondary-gray' weight='semi-bold'>
                    <Trans i18nKey='app.purchase.cart_page.table.product' />
                  </Text>
                </td>
                <td className='p-3' style={{ width: '25%' }}>
                  <Text heading='xs' color='secondary-gray' weight='semi-bold'>
                    <Trans i18nKey='app.purchase.cart_page.table.source' />
                  </Text>
                </td>
                <td className='p-3' style={{ width: '20%' }} align='right'>
                  <Text heading='xs' color='secondary-gray' weight='semi-bold'>
                    <Trans i18nKey='app.common.quantity' />
                  </Text>
                </td>
              </tr>
            </thead>
            <tbody>
              {sortBy(items, (o) => !o.errors.ERR_QUANTITY_0).map((error) => (
                <ItemRow
                  key={error.item.id}
                  item={error.item}
                  reason={
                    WcwCartHelpers.isTechnicalError(error.errors)
                      ? 'ERROR'
                      : 'UNAVAILABLE'
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer variant='white'>
        <Button
          id='wcw-go-to-search'
          variant='primary'
          as={Link}
          onClick={onClose}
          to='../search'
          fullWidth
        >
          <Icon icon='reload' size='sm' />
          <Text heading='normal' weight='semi-bold' color='white'>
            <Trans i18nKey='app.purchase.cart_page.unavailable_modal.re_search' />
          </Text>
        </Button>
      </Modal.Footer>
    </Modal.Container>
  );
}

export default WcwUnavailableItemsModal;
